import {
  ApiShowDataTableFormat,
  ClassNameKey,
  ColumnType,
  DashboardObjectType,
  DataType,
  DateRangeType,
  DateStepPeriod,
  DomainRestrictionType,
  EncodingType,
  ErrorCodes,
  ExtensionContext,
  ExtensionDialogResult,
  ExtensionMode,
  FieldAggregationType,
  FieldRoleType,
  FilterDomainType,
  FilterNullOption,
  FilterUpdateType,
  HierarchicalLevelSelectionState,
  MarkType,
  SelectOptions,
} from '@tableau/api-internal-contract-js';
import * as Enums from '../../gen-src/preslayer/Enums';
import { InternalTableauError } from '../InternalTableauError';

/**
 * This class converts from a source enum value to destination enum
 * value given a mapping from source to destination when constructed.
 *
 * Note: This exact same class is defined in api-shared.  Given its small
 * nature, it is not worth having in a separate project to share this between
 * api-core and api-shared.  If more utility functionality is added that is used by api-core
 * and api-shared but has no other dependecies, a utiltity project might be merited,
 * and this class could be moved.
 *
 * smann note (2/1/2019): Just updated this code and it's only a little bit of a pain
 *  keeping it in sync. Currently less of a pain than adding a shared library dependency,
 *  but this might change. Leaving this note for tracking purposes.
 */
export class EnumConverter<TSourceType extends string, TDestinationType> {
  public constructor(private _mappings: { [enumVal: string]: TDestinationType }, private _defaultVal?: TDestinationType) {}

  public convert(enumVal: TSourceType, throwIfMissing: ShouldThrow = ShouldThrow.Yes): TDestinationType {
    if (this._mappings.hasOwnProperty(enumVal)) {
      return this._mappings[enumVal as string];
    }

    if (this._defaultVal !== undefined && throwIfMissing !== ShouldThrow.Yes) {
      return this._defaultVal;
    }

    throw new InternalTableauError(ErrorCodes.MISSING_ENUM_MAPPING, `Enum Mapping not found for: ${enumVal}`);
  }
}

export enum ShouldThrow {
  Yes = 'yes',
  No = 'no',
}

/* tslint:disable:typedef - Disable this to make declaring these classes a bit easier */
export class PresLayerToApiEnumMappings {
  public static zoneType = new EnumConverter<Enums.ZoneType, DashboardObjectType>(
    {
      [Enums.ZoneType.Invalid]: DashboardObjectType.Blank,
      [Enums.ZoneType.Viz]: DashboardObjectType.Worksheet,
      [Enums.ZoneType.ColorLegend]: DashboardObjectType.Legend,
      [Enums.ZoneType.ShapeLegend]: DashboardObjectType.Legend,
      [Enums.ZoneType.SizeLegend]: DashboardObjectType.Legend,
      [Enums.ZoneType.MapLegend]: DashboardObjectType.Legend,
      [Enums.ZoneType.Filter]: DashboardObjectType.QuickFilter,
      [Enums.ZoneType.Highlighter]: DashboardObjectType.Blank, // TODO
      [Enums.ZoneType.CurrPage]: DashboardObjectType.PageFilter,
      [Enums.ZoneType.Empty]: DashboardObjectType.Blank,
      [Enums.ZoneType.Title]: DashboardObjectType.Title,
      [Enums.ZoneType.Text]: DashboardObjectType.Text,
      [Enums.ZoneType.Bitmap]: DashboardObjectType.Image,
      [Enums.ZoneType.Web]: DashboardObjectType.WebPage,
      // Could be extension or button. Conditionally converts to Extension in PresLayerToExtensionsApiConverter
      [Enums.ZoneType.DashboardObject]: DashboardObjectType.Blank,
      [Enums.ZoneType.ParamCtrl]: DashboardObjectType.ParameterControl,
      [Enums.ZoneType.FlipboardNav]: DashboardObjectType.Blank,
      [Enums.ZoneType.Flipboard]: DashboardObjectType.Blank,
      [Enums.ZoneType.LayoutBasic]: DashboardObjectType.Blank,
      [Enums.ZoneType.LayoutFlow]: DashboardObjectType.Blank,
      [Enums.ZoneType.LayoutFreeForm]: DashboardObjectType.Blank,
      [Enums.ZoneType.End]: DashboardObjectType.Blank,
      [Enums.ZoneType.SetMembership]: DashboardObjectType.Blank,
      [Enums.ZoneType.LayoutFlow]: DashboardObjectType.Blank,
      [Enums.ZoneType.LayoutCarousel]: DashboardObjectType.Blank,
      [Enums.ZoneType.Slide]: DashboardObjectType.Blank,
      [Enums.ZoneType.LayoutZIndex]: DashboardObjectType.Blank,
    },
    DashboardObjectType.Blank,
  );

  public static extensionContext = new EnumConverter<Enums.AddInContext, ExtensionContext>({
    [Enums.AddInContext.Unknown]: ExtensionContext.Unknown,
    [Enums.AddInContext.Desktop]: ExtensionContext.Desktop,
    [Enums.AddInContext.Server]: ExtensionContext.Server,
  });

  public static extensionMode = new EnumConverter<Enums.AddInMode, ExtensionMode>({
    [Enums.AddInMode.Unknown]: ExtensionMode.Unknown,
    [Enums.AddInMode.Authoring]: ExtensionMode.Authoring,
    [Enums.AddInMode.Viewing]: ExtensionMode.Viewing,
  });

  public static extensionDialogResult = new EnumConverter<Enums.ExtensionDialogResult, ExtensionDialogResult>({
    [Enums.ExtensionDialogResult.DialogAlreadyOpen]: ExtensionDialogResult.DialogAlreadyOpen,
    [Enums.ExtensionDialogResult.InvalidDomain]: ExtensionDialogResult.InvalidDomain,
    [Enums.ExtensionDialogResult.Success]: ExtensionDialogResult.Success,
  });

  public static classNameKey = new EnumConverter<Enums.ClassNameKey, ClassNameKey>({
    [Enums.ClassNameKey.DashboardTitle]: ClassNameKey.DashboardTitle,
    [Enums.ClassNameKey.StoryTitle]: ClassNameKey.StoryTitle,
    [Enums.ClassNameKey.Tooltip]: ClassNameKey.Tooltip,
    [Enums.ClassNameKey.Worksheet]: ClassNameKey.Worksheet,
    [Enums.ClassNameKey.WorksheetTitle]: ClassNameKey.WorksheetTitle,
  });

  public static dataType = new EnumConverter<Enums.DataType, DataType>({
    [Enums.DataType.Boolean]: DataType.Bool,
    [Enums.DataType.Date]: DataType.Date,
    [Enums.DataType.DateTime]: DataType.DateTime,
    [Enums.DataType.Integer]: DataType.Int,
    [Enums.DataType.Real]: DataType.Float,
    [Enums.DataType.String]: DataType.String,
    [Enums.DataType.Spatial]: DataType.Spatial,
    [Enums.DataType.Tuple]: DataType.String,
    [Enums.DataType.Unknown]: DataType.Unknown,
  });

  public static columnType = new EnumConverter<Enums.FieldType, ColumnType>({
    [Enums.FieldType.Continuous]: ColumnType.Continuous,
    [Enums.FieldType.Nominal]: ColumnType.Discrete,
    [Enums.FieldType.Ordinal]: ColumnType.Discrete,
    [Enums.FieldType.Unknown]: ColumnType.Unknown,
  });

  public static fieldAggregationType = new EnumConverter<Enums.AggType, FieldAggregationType>({
    [Enums.AggType.Attr]: FieldAggregationType.Attr,
    [Enums.AggType.Avg]: FieldAggregationType.Avg,
    [Enums.AggType.Collect]: FieldAggregationType.Collect,
    [Enums.AggType.Count]: FieldAggregationType.Count,
    [Enums.AggType.CountD]: FieldAggregationType.Countd,
    [Enums.AggType.Day]: FieldAggregationType.Day,
    [Enums.AggType.End]: FieldAggregationType.End,
    [Enums.AggType.Hour]: FieldAggregationType.Hour,
    [Enums.AggType.InOut]: FieldAggregationType.InOut,
    [Enums.AggType.Kurtosis]: FieldAggregationType.Kurtosis,
    [Enums.AggType.Max]: FieldAggregationType.Max,
    [Enums.AggType.MDY]: FieldAggregationType.Mdy,
    [Enums.AggType.Median]: FieldAggregationType.Median,
    [Enums.AggType.Min]: FieldAggregationType.Min,
    [Enums.AggType.Minute]: FieldAggregationType.Minute,
    [Enums.AggType.MonthYear]: FieldAggregationType.MonthYear,
    [Enums.AggType.None]: FieldAggregationType.None,
    [Enums.AggType.Qtr]: FieldAggregationType.Qtr,
    [Enums.AggType.Quart1]: FieldAggregationType.Quart1,
    [Enums.AggType.Quart3]: FieldAggregationType.Quart3,
    [Enums.AggType.Second]: FieldAggregationType.Second,
    [Enums.AggType.Skewness]: FieldAggregationType.Skewness,
    [Enums.AggType.Stdev]: FieldAggregationType.Stdev,
    [Enums.AggType.StdevP]: FieldAggregationType.Stdevp,
    [Enums.AggType.Sum]: FieldAggregationType.Sum,
    [Enums.AggType.TruncDay]: FieldAggregationType.TruncDay,
    [Enums.AggType.TruncHour]: FieldAggregationType.TruncHour,
    [Enums.AggType.TruncMinute]: FieldAggregationType.TruncMinute,
    [Enums.AggType.TruncMonth]: FieldAggregationType.TruncMonth,
    [Enums.AggType.TruncQtr]: FieldAggregationType.TruncQtr,
    [Enums.AggType.TruncSecond]: FieldAggregationType.TruncSecond,
    [Enums.AggType.TruncWeek]: FieldAggregationType.TruncWeek,
    [Enums.AggType.TruncYear]: FieldAggregationType.TruncYear,
    [Enums.AggType.User]: FieldAggregationType.User,
    [Enums.AggType.Var]: FieldAggregationType.Var,
    [Enums.AggType.VarP]: FieldAggregationType.Varp,
    [Enums.AggType.Week]: FieldAggregationType.Week,
    [Enums.AggType.Weekday]: FieldAggregationType.Weekday,
    [Enums.AggType.Year]: FieldAggregationType.Year,
  });

  public static fieldRoleType = new EnumConverter<Enums.FieldRole, FieldRoleType>({
    [Enums.FieldRole.Dimension]: FieldRoleType.Dimension,
    [Enums.FieldRole.Measure]: FieldRoleType.Measure,
    [Enums.FieldRole.Unknown]: FieldRoleType.Unknown,
  });

  public static parameterTypesDomainType = new EnumConverter<Enums.ParameterTypesDomainType, DomainRestrictionType>({
    [Enums.ParameterTypesDomainType.Any]: DomainRestrictionType.All,
    [Enums.ParameterTypesDomainType.List]: DomainRestrictionType.List,
    [Enums.ParameterTypesDomainType.Range]: DomainRestrictionType.Range,
  });

  public static dateStepPeriod = new EnumConverter<Enums.DatePeriodType, DateStepPeriod>({
    [Enums.DatePeriodType.PeriodYear]: DateStepPeriod.Years,
    [Enums.DatePeriodType.PeriodQuarter]: DateStepPeriod.Quarters,
    [Enums.DatePeriodType.PeriodMonth]: DateStepPeriod.Months,
    [Enums.DatePeriodType.PeriodWeek]: DateStepPeriod.Weeks,
    [Enums.DatePeriodType.PeriodDay]: DateStepPeriod.Days,
    [Enums.DatePeriodType.PeriodHour]: DateStepPeriod.Hours,
    [Enums.DatePeriodType.PeriodMinute]: DateStepPeriod.Minutes,
    [Enums.DatePeriodType.PeriodSecond]: DateStepPeriod.Seconds,
  });

  public static dateRangeType = new EnumConverter<Enums.FiltersRelativeDateRangeType, DateRangeType>({
    [Enums.FiltersRelativeDateRangeType.RangeCurrent]: DateRangeType.Current,
    [Enums.FiltersRelativeDateRangeType.RangeCurrentToDate]: DateRangeType.ToDate,
    [Enums.FiltersRelativeDateRangeType.RangeLast1]: DateRangeType.Last,
    [Enums.FiltersRelativeDateRangeType.RangeLastN]: DateRangeType.LastN,
    [Enums.FiltersRelativeDateRangeType.RangeNext1]: DateRangeType.Next,
    [Enums.FiltersRelativeDateRangeType.RangeNextN]: DateRangeType.NextN,
  });

  public static filterDomainType = new EnumConverter<Enums.FiltersDomainType, FilterDomainType>({
    [Enums.FiltersDomainType.Relevant]: FilterDomainType.Relevant,
    [Enums.FiltersDomainType.Database]: FilterDomainType.Database,
  });

  public static hierarchicalLevelSelectionStateType = new EnumConverter<Enums.HSMLevelSelectionState, HierarchicalLevelSelectionState>({
    [Enums.HSMLevelSelectionState.AllSelected]: HierarchicalLevelSelectionState.AllSelected,
    [Enums.HSMLevelSelectionState.NoneSelected]: HierarchicalLevelSelectionState.NoneSelected,
    [Enums.HSMLevelSelectionState.SomeSelected]: HierarchicalLevelSelectionState.SomeSelected,
    [Enums.HSMLevelSelectionState.UnknownSelected]: HierarchicalLevelSelectionState.UnknownSelected,
  });

  public static primitiveType = new EnumConverter<Enums.PrimitiveType, MarkType>({
    [Enums.PrimitiveType.PT_AREA]: MarkType.Area,
    [Enums.PrimitiveType.PT_BAR]: MarkType.Bar,
    [Enums.PrimitiveType.PT_CIRCLE]: MarkType.Circle,
    [Enums.PrimitiveType.PT_GANTT]: MarkType.GanttBar,
    [Enums.PrimitiveType.PT_HEATMAP]: MarkType.Heatmap,
    [Enums.PrimitiveType.PT_LINE]: MarkType.Line,
    [Enums.PrimitiveType.PT_MULTIPOLYGON]: MarkType.Map,
    [Enums.PrimitiveType.PT_PIE]: MarkType.Pie,
    [Enums.PrimitiveType.PT_POLYGON]: MarkType.Polygon,
    [Enums.PrimitiveType.PT_SHAPE]: MarkType.Shape,
    [Enums.PrimitiveType.PT_SQUARE]: MarkType.Square,
    [Enums.PrimitiveType.PT_TEXT]: MarkType.Text,
    [Enums.PrimitiveType.PT_VIZ_EXTENSION]: MarkType.VizExtension,
  });

  public static encodingType = new EnumConverter<Enums.EncodingType, EncodingType>({
    [Enums.EncodingType.ET_COLOR]: EncodingType.Color,
    [Enums.EncodingType.ET_CUSTOM]: EncodingType.Custom,
    [Enums.EncodingType.ET_GEOMETRY]: EncodingType.Geometry,
    [Enums.EncodingType.ET_LOD]: EncodingType.Detail,
    [Enums.EncodingType.ET_SHAPE]: EncodingType.Shape,
    [Enums.EncodingType.ET_SIZE]: EncodingType.Size,
    [Enums.EncodingType.ET_TEXT]: EncodingType.Label,
    [Enums.EncodingType.ET_TOOLTIP]: EncodingType.Tooltip,
    [Enums.EncodingType.ET_WEDGESIZE]: EncodingType.Angle,
  });
}

export class ApiToPresLayerEnumMappings {
  public static filterDomainType = new EnumConverter<FilterDomainType, Enums.FiltersDomainType>({
    [FilterDomainType.Relevant]: Enums.FiltersDomainType.Relevant,
    [FilterDomainType.Database]: Enums.FiltersDomainType.Database,
  });

  public static filterUpdateType = new EnumConverter<FilterUpdateType, Enums.FilterUpdateType>({
    [FilterUpdateType.Add]: Enums.FilterUpdateType.ADD,
    [FilterUpdateType.All]: Enums.FilterUpdateType.ALL,
    [FilterUpdateType.Replace]: Enums.FilterUpdateType.REPLACE,
    [FilterUpdateType.Remove]: Enums.FilterUpdateType.REMOVE,
  });

  public static nullOption = new EnumConverter<FilterNullOption, Enums.FiltersQuantitativeIncludedValues>({
    [FilterNullOption.AllValues]: Enums.FiltersQuantitativeIncludedValues.All,
    [FilterNullOption.NonNullValues]: Enums.FiltersQuantitativeIncludedValues.NonNull,
    [FilterNullOption.NullValues]: Enums.FiltersQuantitativeIncludedValues.Null,
  });

  public static showDataTableFormat = new EnumConverter<ApiShowDataTableFormat, Enums.ShowDataTableFormat>({
    [ApiShowDataTableFormat.NativeAndFormattedValues]: Enums.ShowDataTableFormat.NativeAndFormattedValuesDataDictionary,
    [ApiShowDataTableFormat.NativeValuesOnly]: Enums.ShowDataTableFormat.NativeValuesOnlyDataDictionary,
    [ApiShowDataTableFormat.FormattedValuesOnly]: Enums.ShowDataTableFormat.FormattedValuesOnlyDataDictionary,
  });

  public static periodType = new EnumConverter<DateStepPeriod, Enums.DatePeriodType>({
    [DateStepPeriod.Years]: Enums.DatePeriodType.PeriodYear,
    [DateStepPeriod.Quarters]: Enums.DatePeriodType.PeriodQuarter,
    [DateStepPeriod.Months]: Enums.DatePeriodType.PeriodMonth,
    [DateStepPeriod.Weeks]: Enums.DatePeriodType.PeriodWeek,
    [DateStepPeriod.Days]: Enums.DatePeriodType.PeriodDay,
    [DateStepPeriod.Hours]: Enums.DatePeriodType.PeriodHour,
    [DateStepPeriod.Minutes]: Enums.DatePeriodType.PeriodMinute,
    [DateStepPeriod.Seconds]: Enums.DatePeriodType.PeriodSecond,
  });

  public static dateRangeType = new EnumConverter<DateRangeType, Enums.FiltersRelativeDateRangeType>({
    [DateRangeType.Last]: Enums.FiltersRelativeDateRangeType.RangeLast1,
    [DateRangeType.LastN]: Enums.FiltersRelativeDateRangeType.RangeLastN,
    [DateRangeType.Next]: Enums.FiltersRelativeDateRangeType.RangeNext1,
    [DateRangeType.NextN]: Enums.FiltersRelativeDateRangeType.RangeNextN,
    [DateRangeType.Current]: Enums.FiltersRelativeDateRangeType.RangeCurrent,
    [DateRangeType.ToDate]: Enums.FiltersRelativeDateRangeType.RangeCurrentToDate,
  });

  public static selectOptionsType = new EnumConverter<SelectOptions, Enums.SelectOptions>({
    [SelectOptions.Simple]: Enums.SelectOptions.SelectOptionsSimple,
    [SelectOptions.Toggle]: Enums.SelectOptions.SelectOptionsToggle,
  });
}
/* tslint:enable:typedef */
